import React, { useState,useEffect } from 'react';
import ReactSpeedometer from 'react-d3-speedometer';
import {
  Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem, Button,
  InputAdornment, useTheme, Grid, Card, CardContent, Table, TableBody, TableCell,
  TableRow,
  Paper
} from '@mui/material';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';

const componentLibrary = {
  batteries: [
    { 
      type: 'LiPo 850mAh - 80/120C', 
      capacity: 850, 
      cRateCont: 80, 
      cRateMax: 120,
      weight: 100, 
      resistance: 0.02, 
      voltage: 3.7,
      chemistry: 'Lithium Polymer',
      dischargeCurve: 'Steep'
    },
    { 
      type: 'LiPo 1300mAh - 45/90C', 
      capacity: 1300, 
      cRateCont: 45, 
      cRateMax: 90, 
      weight: 150, 
      resistance: 0.015, 
      voltage: 3.7,
      chemistry: 'Lithium Polymer',
      dischargeCurve: 'Moderate'
    },
    { 
      type: 'LiIon 3000mAh - 10/20A', 
      capacity: 3000, 
      cRateCont: 10, 
      cRateMax: 20, 
      weight: 200, 
      resistance: 0.03, 
      voltage: 3.6,
      chemistry: 'Lithium Ion',
      dischargeCurve: 'Flat'
    }
  ],
  controllers: [
    {
      type: 'max 10A',
      currentCont: 35,
      currentMax: 45,
      resistance: 0.005,
      weight: 12,
      protocol: 'DShot600',
      firmware: 'BLHeli_32'
    },
    {
      type: 'max 20A',
      currentCont: 20,
      currentMax: 25,
      resistance: 0.008,
      weight: 8,
      protocol: 'DShot300',
      firmware: 'BLHeli_S'
    }
  ],
  motors: [
    { 
      type: 'actro', 
      kv: 2300, 
      resistance: 0.15, 
      noLoadCurrent: 0.5, 
      weight: 32,
      maxPower: 180,
      statorSize: '22x4mm'
    },
    { 
      type: 'airtek', 
      kv: 700, 
      resistance: 0.08, 
      noLoadCurrent: 1.2, 
      weight: 82,
      maxPower: 350,
      statorSize: '35x10mm'
    }
  ],
  propellers: [
    { 
      type: 'Falcon', 
      diameter: 10, 
      pitch: 4.7, 
      blades: 2, 
      pConst: 1.2, 
      tConst: 1.0,
      material: 'Composite',
      weight: 15
    },
    { 
      type: 'APC Speed E', 
      diameter: 9, 
      pitch: 4.5, 
      blades: 2, 
      pConst: 1.1, 
      tConst: 0.9,
      material: 'Composite',
      weight: 12
    }
  ]
};

const CalculateThrust = () => {
    const theme = useTheme();

    const [formData, setFormData] = useState({
      modelWeight: 850,
      modelWeightUnit: 'g',
      numRotors: 4,
      frameSize: 400,
      fcuTiltLimit: 'no limit',
      fieldElevation: 500,
      airTemperature: 25,
      pressure: 1013,
      batteryCellType: 'select...',
      batteryChargeState: 'normal',
      batteryConfiguration: { s: 3, p: 1 },
      controllerType: 'select...',
      motorType: 'select...',
      propellerType: 'select...',
      current: {
        aCont: 0,
        aMax: 0
      }
    });

    const [performanceMetrics, setPerformanceMetrics] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);

    const updateFormData = (path, value) => {
        setFormData(prev => {
          const pathArray = Array.isArray(path) ? path : path.split('.');
          const newData = { ...prev };
          let current = newData;
        
          for (let i = 0; i < pathArray.length - 1; i++) {
            current = current[pathArray[i]] = { ...current[pathArray[i]] };
          }

          current[pathArray[pathArray.length - 1]] = value;
          return newData;
        });
    };
    
    // Calculate derived battery values
    const calculateBatteryMetrics = () => {
        if (formData.batteryCellType === 'select...') return;

        const battery = componentLibrary.batteries.find(b => b.type === formData.batteryCellType);
        if (!battery) return;

        const { s, p } = formData.batteryConfiguration;

        return {
          cellCapacity: battery.capacity,
          cellCapacityTotal: battery.capacity * p,
          batteryResistance: battery.resistance * s / p,
          batteryVoltage: battery.voltage * s,
          batteryCRateCont: battery.cRateCont,
          batteryCRateMax: battery.cRateMax,
          batteryWeight: battery.weight * s * p
        };
    };

    useEffect(() => {
        const batteryMetrics = calculateBatteryMetrics();
        if (batteryMetrics) {
          Object.entries(batteryMetrics).forEach(([key, value]) => {
            updateFormData(key, value);
          });
        }
    
        if (formData.controllerType !== 'select...') {
          const controller = componentLibrary.controllers.find(c => c.type === formData.controllerType);
          if (controller) {
            updateFormData('current.aCont', controller.currentCont);
            updateFormData('current.aMax', controller.currentMax);
            updateFormData('controllerResistance', controller.resistance);
            updateFormData('controllerWeight', controller.weight);
          }
        }
    
        if (formData.motorType !== 'select...') {
          const motor = componentLibrary.motors.find(m => m.type === formData.motorType);
          if (motor) {
            updateFormData('motorKv', motor.kv);
            updateFormData('noLoadCurrent', motor.noLoadCurrent);
            updateFormData('motorResistance', motor.resistance);
            updateFormData('motorWeight', motor.weight);
          }
        }
    
        if (formData.propellerType !== 'select...') {
          const propeller = componentLibrary.propellers.find(p => p.type === formData.propellerType);
          if (propeller) {
            updateFormData('propellerDiameter', propeller.diameter);
            updateFormData('propellerPitch', propeller.pitch);
            updateFormData('numBlades', propeller.blades);
            updateFormData('pConst', propeller.pConst);
            updateFormData('tConst', propeller.tConst);
          }
        }
    // calculateBatteryMetrics is redefined each render; the form fields below are
    // the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        formData.batteryCellType,
        formData.controllerType,
        formData.motorType,
        formData.propellerType,
        formData.batteryConfiguration.s,
        formData.batteryConfiguration.p
    ]);

    const calculatePerformance = async () => {
        setIsCalculating(true);
        
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
          const { s, p } = formData.batteryConfiguration;
          const battery = componentLibrary.batteries.find(b => b.type === formData.batteryCellType);
          const motor = componentLibrary.motors.find(m => m.type === formData.motorType);
          const propeller = componentLibrary.propellers.find(p => p.type === formData.propellerType);

          if (!battery || !motor || !propeller) {
            throw new Error('Please select all components');
          }

          const totalVoltage = battery.voltage * s;
          const totalCapacity = battery.capacity * p;
          const maxCurrent = totalCapacity * battery.cRateCont / 1000;
          const power = totalVoltage * maxCurrent;

          const thrustCoefficient = 0.8;
          const powerCoefficient = 1.2;
          const airDensity = 1.225; // kg/m³
          const propellerArea = Math.PI * Math.pow(propeller.diameter * 0.0254 / 2, 2);

          const thrust = thrustCoefficient * airDensity * propellerArea * 
                        Math.pow(motor.kv * totalVoltage * propeller.pitch * 0.0254 / 60, 2);
          const powerRequired = powerCoefficient * airDensity * propellerArea * 
                              Math.pow(motor.kv * totalVoltage * propeller.pitch * 0.0254 / 60, 3);

          const hoverTime = (totalCapacity / 1000) / (powerRequired / power * 60);
          const thrustWeightRatio = thrust * formData.numRotors / (formData.modelWeight / 1000 * 9.81);
          const specificThrust = thrust * formData.numRotors / (formData.modelWeight / 1000);
          const efficiency = (thrust * formData.numRotors * 9.81) / powerRequired * 100;

          setPerformanceMetrics({
            load: (maxCurrent / totalCapacity * 1000).toFixed(1),
            hoverFlightTime: hoverTime.toFixed(1),
            electricPower: power.toFixed(0),
            estTemperature: `${25 + power / 100}`,
            thrustWeight: thrustWeightRatio.toFixed(1),
            specificThrust: specificThrust.toFixed(1),
            efficiency: efficiency.toFixed(1),
            batteryMetrics: {
              voltage: `${totalVoltage.toFixed(1)} V`,
              current: `${maxCurrent.toFixed(1)} A`,
              energy: `${(totalVoltage * totalCapacity / 1000).toFixed(1)} Wh`,
              powerDensity: `${(power / (battery.weight * s * p)).toFixed(1)} W/g`
            },
            motorMetrics: {
              rpm: `${(motor.kv * totalVoltage).toFixed(0)} rpm`,
              thrustPerMotor: `${(thrust * 1000).toFixed(0)} g`,
              powerPerMotor: `${(powerRequired / formData.numRotors).toFixed(0)} W`
            },
            systemMetrics: {
              totalThrust: `${(thrust * formData.numRotors * 1000).toFixed(0)} g`,
              powerToWeight: `${(power / (formData.modelWeight / 1000)).toFixed(1)} W/kg`
            }
          });
        } catch (error) {
          console.error('Calculation error:', error);
        } finally {
          setIsCalculating(false);
        }
    };

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleConfigChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        batteryConfiguration: {
          ...prev.batteryConfiguration,
          [name]: value,
        },
      }));
    };

 

    const isDarkMode = theme.palette.mode === 'dark';

    const formContainerStyles = {
      padding: theme.spacing(4),
      backgroundColor: isDarkMode ? theme.palette.primary.main : theme.palette.tertiary.main,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    };

    const sectionBoxStyles = {
      backgroundColor: isDarkMode ? theme.palette.secondary.main : theme.palette.white.main,
      padding: theme.spacing(2),
      border: `1px solid ${isDarkMode ? theme.palette.border.color : theme.palette.border.color}`,
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      flexDirection: 'column',
    };

    const inputFieldStyles = {
      flexGrow: 1,
      '& .MuiInputBase-root': {
        backgroundColor: isDarkMode ? theme.palette.black.main : theme.palette.primary.bg,
        color: isDarkMode ? theme.palette.text.color : theme.palette.text.color,
        height: '40px',
      },
      '& .MuiInputLabel-root': {
        color: isDarkMode ? theme.palette.secondaryText.color : theme.palette.secondaryText.color,
        transform: 'translate(14px, 10px) scale(1)',
      },
      '& .MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isDarkMode ? theme.palette.border.color : theme.palette.border.color,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isDarkMode ? theme.palette.secondary.light : theme.palette.secondary.main,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    };

    const readOnlyFieldStyles = {
      ...inputFieldStyles,
      '& .MuiInputBase-input.Mui-disabled': {
        backgroundColor: isDarkMode ? theme.palette.secondary.main : theme.palette.secondary.main,
        color: isDarkMode ? theme.palette.secondaryText.color : theme.palette.secondaryText.color,
        WebkitTextFillColor: isDarkMode ? theme.palette.secondaryText.color : theme.palette.secondaryText.color,
      },
    };

    const selectFieldStyles = {
      ...inputFieldStyles,
      '& .MuiSelect-select': {
        backgroundColor: isDarkMode ? theme.palette.black.main : theme.palette.primary.bg,
        height: '40px !important',
        boxSizing: 'border-box',
        paddingTop: 0,
        paddingBottom: 0,
        display: 'flex',
        alignItems: 'center',
      },
      '& .MuiInputLabel-root': {
        color: isDarkMode ? theme.palette.secondaryText.color : theme.palette.secondaryText.color,
      },
      '&.Mui-focused .MuiInputLabel-root': {
        color: theme.palette.primary.main,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isDarkMode ? theme.palette.border.color : theme.palette.border.color,
      },
      '& .MuiSvgIcon-root': {
        color: isDarkMode ? theme.palette.secondaryText.color : theme.palette.secondaryText.color,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isDarkMode ? theme.palette.secondary.light : theme.palette.secondary.main,
      },
    };


    const flexContainerStyles = {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    };

    const flexItemStyles = {
      flex: '1 1 200px',
      minWidth: '200px', 
    };

    const exportToPDF = () => {
      const doc = new jsPDF();
      doc.text('Thrust Calculator Report', 10, 10);
      doc.text(`Battery: ${formData.batteryCellType}`, 10, 20);
      doc.text(`Motor: ${formData.motorType}`, 10, 30);
      doc.text(`Propeller: ${formData.propellerType}`, 10, 40);
      doc.save('thrust_calculator_report.pdf');
    };

    const exportToCSV = () => {
      let csvContent = "Parameter,Value,Unit\n";
      csvContent += `Battery Type,${formData.batteryCellType},\n`;
      csvContent += `Hover Flight Time,${performanceMetrics?.hoverFlightTime},min\n`;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'thrust_calculator_data.csv');
    };

    const speedometerProps = {
        needleColor: theme.palette.primary.main,
        textColor: theme.palette.text.primary,
        width: 180,
        height: 130,
        segmentColors: [
          theme.palette.error.main,
          theme.palette.warning.main,
          theme.palette.success.main
        ]
    };

    return (
        <Box sx={formContainerStyles}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: isDarkMode ? theme.palette.text.color : theme.palette.text.color }}>
              Thrust Calculator
            </Typography>

            {/* Battery Cell Section */}
            <Box sx={sectionBoxStyles}>
                <Typography variant="h6" gutterBottom sx={{ color: isDarkMode ? theme.palette.text.color : theme.palette.text.color, my: 2 }}>Battery Cell</Typography>
                <Box sx={flexContainerStyles}>
                  <FormControl sx={flexItemStyles} variant="outlined" size="small">
                    <InputLabel>Type (Cont. / max. C)</InputLabel>
                    <Select
                      label="Type (Cont. / max. C)"
                      name="batteryCellType"
                      value={formData.batteryCellType}
                      sx={{...selectFieldStyles}}
                      onChange={handleInputChange}
                    >
                      <MenuItem value="select...">select...</MenuItem>
                      <MenuItem value="LiPo 850mAh - 80/120C">LiPo 850mAh - 80/120C</MenuItem>
                      <MenuItem value="LiPo 1300mAh - 45/90C">LiPo 1300mAh - 45/90C</MenuItem>
                      <MenuItem value="LiIon 3000mAh - 10/20A">LiIon 3000mAh - 10/20A</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl sx={flexItemStyles} variant="outlined" size="small">
                    <InputLabel>Charge State</InputLabel>
                    <Select
                      label="Charge State"
                      name="batteryChargeState"
                      value={formData.batteryChargeState}
                      onChange={handleInputChange}
                      sx={{...selectFieldStyles}}
                    >
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="full">Full</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                  <Box display="flex" sx={{ flex: '1 1 200px', gap: 1 }}>
                    <TextField
                      label="(S)"
                      name="s"
                      type="number"
                      value={formData.batteryConfiguration.s}
                      onChange={handleConfigChange}
                      variant="outlined"
                      size="small"
                      sx={{ ...inputFieldStyles, flex: '1' }}
                    />
                    <TextField
                      label="(P)"
                      name="p"
                      type="number"
                      value={formData.batteryConfiguration.p}
                      onChange={handleConfigChange}
                      variant="outlined"
                      size="small"
                      sx={{ ...inputFieldStyles, flex: '1' }}
                    />
                  </Box>
                  <TextField
                    label="Cell Capacity"
                    name="cellCapacity"
                    value={formData.cellCapacity}
                    variant="outlined"
                    size="small"
                    sx={flexItemStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">mAh</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Cell Capacity Total"
                    name="cellCapacityTotal"
                    value={formData.cellCapacityTotal}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">mAh</InputAdornment>,
                    }}
                  />
                  <FormControl sx={flexItemStyles} variant="outlined" size="small">
                    <InputLabel>Max Discharge</InputLabel>
                    <Select
                      label="Max Discharge"
                      name="maxDischarge"
                      value={formData.maxDischarge}
                      onChange={handleInputChange}
                      sx={{...selectFieldStyles}}
                    >
                      {[...Array(20).keys()].map((i) => (
                        <MenuItem key={i} value={`${(i + 1) * 5}%`}>{(i + 1) * 5}%</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Resistance"
                    name="batteryResistance"
                    value={formData.batteryResistance}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Ohm</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Voltage"
                    name="batteryVoltage"
                    value={formData.batteryVoltage}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">V</InputAdornment>,
                    }}
                  />
                  <Box display="flex" sx={{ flex: '1 1 200px', gap: 1 }}>
                    <TextField
                      label="C-Rate (cont.)"
                      name="batteryCRateCont"
                      value={formData.batteryCRateCont}
                      variant="outlined"
                      size="small"
                      disabled
                      sx={{ ...readOnlyFieldStyles, flex: '1' }}
                      InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">C</InputAdornment>,
                      }}
                    />
                    <TextField
                      label="(max.)"
                      name="batteryCRateMax"
                      value={formData.batteryCRateMax}
                      variant="outlined"
                      size="small"
                      disabled
                      sx={{ ...readOnlyFieldStyles, flex: '1' }}
                      InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">C</InputAdornment>,
                      }}
                    />
                  </Box>
                  <TextField
                    label="Weight"
                    name="batteryWeight"
                    value={formData.batteryWeight}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                  />
                </Box>
            </Box>

            {/* Controller Section */}
            <Box sx={sectionBoxStyles}>
                <Typography variant="h6" gutterBottom sx={{ color: isDarkMode ? theme.palette.text.color : theme.palette.text.color }}>Controller</Typography>
                <Box sx={flexContainerStyles}>
                  <FormControl sx={flexItemStyles} variant="outlined" size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      name="controllerType"
                      value={formData.controllerType}
                      onChange={handleInputChange}
                      sx={{...selectFieldStyles}}
                    >
                      <MenuItem value="select...">select...</MenuItem>
                      <MenuItem value="max 10A">max 10A</MenuItem>
                      <MenuItem value="max 20A">max 20A</MenuItem>
                      <MenuItem value="max 30A">max 30A</MenuItem>
                      <MenuItem value="max 60A">max 60A</MenuItem>
                      <MenuItem value="APD 40F3">APD 40F3</MenuItem>
                      <MenuItem value="APD 80F3x">APD 80F3x</MenuItem>
                    </Select>
                  </FormControl>
                  <Box display="flex" sx={{ flex: '1 1 200px', gap: 1 }}>
                    <TextField
                      label="Current (A cont.)"
                      name="aCont"
                      value={formData.current.aCont}
                      variant="outlined"
                      size="small"
                      disabled
                      sx={{ ...readOnlyFieldStyles, flex: '1' }}
                    />
                    <TextField
                      label="(A max.)"
                      name="aMax"
                      value={formData.current.aMax}
                      variant="outlined"
                      size="small"
                      disabled
                      sx={{ ...readOnlyFieldStyles, flex: '1' }}
                    />
                  </Box>
                  <TextField
                    label="Resistance"
                    name="controllerResistance"
                    value={formData.controllerResistance}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Ohm</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Weight"
                    name="controllerWeight"
                    value={formData.controllerWeight}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                  />
                </Box>
            </Box>

            {/* Motor Section */}
            <Box sx={sectionBoxStyles}>
                <Typography variant="h6" gutterBottom sx={{ color: isDarkMode ? theme.palette.text.color : theme.palette.text.color }}>Motor</Typography>
                <Box sx={flexContainerStyles}>
                  <FormControl sx={flexItemStyles} variant="outlined" size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      name="motorType"
                      value={formData.motorType}
                      onChange={handleInputChange}
                      sx={{...selectFieldStyles}}
                    >
                      <MenuItem value="select...">select...</MenuItem>
                      <MenuItem value="actro">actro</MenuItem>
                      <MenuItem value="airtek">airtek</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Motor KV"
                    name="motorKv"
                    value={formData.motorKv}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                  />
                  <TextField
                    label="No-load Current"
                    name="noLoadCurrent"
                    value={formData.noLoadCurrent}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">A</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Motor Limit"
                    name="motorLimit"
                    value={formData.motorLimit}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                  />
                  <TextField
                    label="Motor Resistance"
                    name="motorResistance"
                    value={formData.motorResistance}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Ohm</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Case Length"
                    name="caseLength"
                    value={formData.caseLength}
                    variant="outlined"
                    size="small"
                    sx={inputFieldStyles}
                  />
                  <TextField
                    label="# Mag Poles"
                    name="numMagPoles"
                    value={formData.numMagPoles}
                    variant="outlined"
                    size="small"
                    sx={inputFieldStyles}
                  />
                  <TextField
                    label="Weight"
                    name="motorWeight"
                    value={formData.motorWeight}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={readOnlyFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                  />
                </Box>
            </Box>

            {/* Propeller Section */}
            <Box sx={sectionBoxStyles}>
                <Typography variant="h6" gutterBottom sx={{ color: isDarkMode ? theme.palette.text.color : theme.palette.text.color }}>Propeller</Typography>
                <Box sx={flexContainerStyles}>
                  <FormControl sx={flexItemStyles} variant="outlined" size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      name="propellerType"
                      value={formData.propellerType}
                      onChange={handleInputChange}
                      sx={{...selectFieldStyles}}
                    >
                      <MenuItem value="select...">select...</MenuItem>
                      <MenuItem value="Falcon">Falcon</MenuItem>
                      <MenuItem value="APC Speed E">APC Speed E</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Diameter"
                    name="propellerDiameter"
                    type="number"
                    value={formData.propellerDiameter}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={inputFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">inch</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Pitch"
                    name="propellerPitch"
                    type="number"
                    value={formData.propellerPitch}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={inputFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">inch</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="# Blades"
                    name="numBlades"
                    type="number"
                    value={formData.numBlades}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={inputFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                  />
                  <TextField
                    label="P const"
                    name="pConst"
                    type="number"
                    value={formData.pConst}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={inputFieldStyles}
                    InputLabelProps={{
                        shrink: !!formData.batteryResistance,
                    }}
                  />
                  <TextField
                    label="T const"
                    name="tConst"
                    type="number"
                    value={formData.tConst}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={inputFieldStyles}
                  />
                  <TextField
                    label="Gear Ratio"
                    name="gearRatio"
                    type="number"
                    value={formData.gearRatio}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={inputFieldStyles}
                  />
                </Box>
            </Box>

            {performanceMetrics && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom color={theme.palette.text.primary}>
                      Performance Metrics
                    </Typography>

                    <Grid container spacing={3}>
                      {/* Speedometers with 3-color segments */}
                      <Grid container spacing={3} sx={{ mb: 4 }}>
                        {[
                          { label: 'Load', value: performanceMetrics.load, unit: 'C', min: 0, max: 100 },
                          { label: 'Hover Time', value: performanceMetrics.hoverFlightTime, unit: 'min', min: 0, max: 30 },
                          { label: 'Power', value: performanceMetrics.electricPower, unit: 'W', min: 0, max: 1000 },
                          { label: 'Temperature', value: performanceMetrics.estTemperature, unit: '°C', min: 20, max: 80 },
                          { label: 'Thrust/Weight', value: performanceMetrics.thrustWeight, unit: 'ratio', min: 0, max: 5 },
                          { label: 'Specific Thrust', value: performanceMetrics.specificThrust, unit: 'g/W', min: 0, max: 10 }
                        ].map((metric, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                              <Typography variant="subtitle1" align="center" gutterBottom>
                                {metric.label}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <ReactSpeedometer
                                  value={parseFloat(metric.value)}
                                  minValue={metric.min}
                                  maxValue={metric.max}
                                  segments={3}
                                  segmentColors={[
                                    theme.palette.warning.main,  
                                    theme.palette.warning.light,  
                                    theme.palette.success.main    
                                  ]}
                                  currentValueText={`${metric.value} ${metric.unit}`}
                                  {...speedometerProps}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    
                      {/* Metrics Tables */}
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6">Battery Performance</Typography>
                              </Box>

                              <Table size="small">
                                <TableBody>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Load</TableCell>
                                    <TableCell>{performanceMetrics.load}</TableCell>
                                    <TableCell>C</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Voltage</TableCell>
                                    <TableCell>{performanceMetrics.batteryMetrics.voltage.split(' ')[0]}</TableCell>
                                    <TableCell>V</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Rated Voltage</TableCell>
                                    <TableCell>{performanceMetrics.batteryMetrics.voltage.split(' ')[0]}</TableCell>
                                    <TableCell>V</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Energy</TableCell>
                                    <TableCell>{performanceMetrics.batteryMetrics.energy.split(' ')[0]}</TableCell>
                                    <TableCell>Wh</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Total Capacity</TableCell>
                                    <TableCell>{formData.cellCapacityTotal}</TableCell>
                                    <TableCell>mAh</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Used Capacity</TableCell>
                                    <TableCell>{(formData.cellCapacityTotal * 0.8).toFixed(0)}</TableCell>
                                    <TableCell>mAh</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Min. Flight Time</TableCell>
                                    <TableCell>{(performanceMetrics.hoverFlightTime * 0.7).toFixed(1)}</TableCell>
                                    <TableCell>min</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Mixed Flight Time</TableCell>
                                    <TableCell>{(performanceMetrics.hoverFlightTime * 0.85).toFixed(1)}</TableCell>
                                    <TableCell>min</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Hover Flight Time</TableCell>
                                    <TableCell>{performanceMetrics.hoverFlightTime}</TableCell>
                                    <TableCell>min</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Weight</TableCell>
                                    <TableCell>{formData.batteryWeight}</TableCell>
                                    <TableCell>g</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                                    <TableCell>{(formData.batteryWeight / 28.35).toFixed(1)}</TableCell>
                                    <TableCell>oz</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>Motor Performance</Typography>

                              <Table size="small">
                                <TableBody>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Current</TableCell>
                                    <TableCell>{performanceMetrics.batteryMetrics.current.split(' ')[0]}</TableCell>
                                    <TableCell>A</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Voltage</TableCell>
                                    <TableCell>{performanceMetrics.batteryMetrics.voltage.split(' ')[0]}</TableCell>
                                    <TableCell>V</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Revolutions</TableCell>
                                    <TableCell>{performanceMetrics.motorMetrics.rpm.split(' ')[0]}</TableCell>
                                    <TableCell>rpm</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Electric Power</TableCell>
                                    <TableCell>{performanceMetrics.electricPower}</TableCell>
                                    <TableCell>W</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Mech. Power</TableCell>
                                    <TableCell>{(performanceMetrics.electricPower * 0.85).toFixed(1)}</TableCell>
                                    <TableCell>W</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Efficiency</TableCell>
                                    <TableCell>{performanceMetrics.efficiency}</TableCell>
                                    <TableCell>%</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Box>
                        <Box sx={{p:2,}}>
                          <Button 
                            variant="contained" 
                            startIcon={<PictureAsPdfIcon />} 
                            onClick={exportToPDF}
                            size="small"
                            color='info'
                            sx={{ mr: 1 }}
                          >
                            Downalod as PDF
                          </Button>
                          <Button 
                            variant="contained" 
                            color='primary'
                            startIcon={<GridOnIcon />} 
                            onClick={exportToCSV}
                            size="small"
                          >
                            Download as CSV
                          </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Calculate Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button variant="contained" onClick={calculatePerformance} color='success'>
                    {isCalculating ? 'Calculating...' : 'Calculate Performance'}
                </Button>
            </Box>
        </Box>
  );
};

export default CalculateThrust;